import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  makeStyles,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import { InfoCard } from '@backstage/core-components';
import { useTranslation } from 'react-i18next';
import { listOrgAdminFiles, type ProjectsApiOptions } from '../../api/projects';
import { ContextFileStatusChip } from '../projects/ContextFileStatusChip';
import { OrgAdminUploadDialog } from './OrgAdminUploadDialog';
import { DeleteOrgFileDialog } from './DeleteOrgFileDialog';
import { type ContextFileStatus, type OrgAdminFile } from '../projects/types';

const IN_PROGRESS_STATUSES: ContextFileStatus[] = [
  'PENDING',
  'QUEUED',
  'VALIDATING',
  'EXTRACTING',
  'INDEXING',
];

const STATUS_PROGRESSION: ContextFileStatus[] = [
  'PENDING',
  'QUEUED',
  'VALIDATING',
  'EXTRACTING',
  'INDEXING',
  'READY',
];

const POLL_INTERVAL_MS = 5000;
const PREVIEW_TICK_MS = 2000;

const MOCK_ORG_ADMIN_FILES: OrgAdminFile[] = [
  {
    id: 'org-admin-1',
    name: 'company-coding-standard.pdf',
    fileTypeTag: 'GENERAL',
    status: 'READY',
    isDefault: true,
    version: 1,
    uploader: { userId: 1, displayName: 'Admin' },
    errorReport: null,
  },
  {
    id: 'org-admin-2',
    name: 'sys2-requirements-template.reqif',
    fileTypeTag: 'SYS2_REQUIREMENT',
    status: 'READY',
    isDefault: false,
    version: 2,
    uploader: { userId: 1, displayName: 'Admin' },
    errorReport: null,
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  subtle: {
    color: theme.palette.text.secondary,
  },
  failedRow: {
    backgroundColor: `${theme.palette.error.light}22`,
  },
  snackbarError: {
    backgroundColor: theme.palette.error.dark,
  },
}));

type Props = {
  orgId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
};

export function OrgAdminFilesPanel({
  orgId,
  apiOptions,
  isPreviewMode,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');

  const [files, setFiles] = useState<OrgAdminFile[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<OrgAdminFile | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [scanFailedMsg, setScanFailedMsg] = useState<string | undefined>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifiedFailedIds = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);
  // Mirrors files state so loadFiles can read the previous value without a
  // state updater function (calling setNotification inside a state updater
  // is a side-effect that React may call multiple times and silently drop).
  const prevFilesRef = useRef<OrgAdminFile[]>([]);

  const prevOrgIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Only reset on subsequent orgId changes, not on initial mount
    // (initial mount is handled by the loadFiles effect below).
    if (prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== orgId) {
      notifiedFailedIds.current = new Set();
      initialLoadDone.current = false;
      setFiles([]);
      setLoadError(undefined);
      setScanFailedMsg(undefined);
    }
    prevOrgIdRef.current = orgId;
  }, [orgId]);

  const hasInProgress = (entries: OrgAdminFile[]) =>
    entries.some(f => IN_PROGRESS_STATUSES.includes(f.status));

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const checkForNewFailures = (prev: OrgAdminFile[], next: OrgAdminFile[]) => {
    // On first load, silently register all pre-existing FAILED files so we
    // never notify about failures that were already there before this session.
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      next
        .filter(f => f.status === 'FAILED')
        .forEach(f => notifiedFailedIds.current.add(f.id));
      return;
    }
    const prevById = new Map(prev.map(f => [f.id, f]));
    for (const f of next) {
      if (
        f.status === 'FAILED' &&
        !notifiedFailedIds.current.has(f.id) &&
        prevById.get(f.id)?.status !== 'FAILED'
      ) {
        notifiedFailedIds.current.add(f.id);
        setScanFailedMsg(
          t('orgAdmin.filesPanel.scanFailedNotification', { fileName: f.name }),
        );
        break; // one notification at a time; next poll will catch remaining
      }
    }
  };

  const loadFiles = useCallback(async () => {
    try {
      const entries = await listOrgAdminFiles(apiOptions, orgId);
      setLoadError(undefined);
      checkForNewFailures(prevFilesRef.current, entries);
      prevFilesRef.current = entries;
      setFiles(entries);
      if (!hasInProgress(entries)) {
        stopPolling();
      }
    } catch {
      setLoadError(t('contextFiles.loadError'));
    }
    // checkForNewFailures and prevFilesRef read only refs — safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiOptions, orgId, t]);

  const startPolling = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(() => {
      void loadFiles();
    }, POLL_INTERVAL_MS);
  }, [loadFiles]);

  useEffect(() => {
    // Reset per-session tracking whenever the org context changes.
    notifiedFailedIds.current = new Set();
    initialLoadDone.current = false;
    prevFilesRef.current = [];

    if (isPreviewMode) {
      setFiles(MOCK_ORG_ADMIN_FILES);
    } else {
      void loadFiles();
    }
    return stopPolling;
  }, [isPreviewMode, loadFiles]);

  useEffect(() => {
    if (!isPreviewMode) return undefined;
    if (!hasInProgress(files)) return undefined;

    const tick = setInterval(() => {
      setFiles(prev => {
        const updated = prev.map(f => {
          if (!IN_PROGRESS_STATUSES.includes(f.status)) return f;
          const idx = STATUS_PROGRESSION.indexOf(f.status);
          const next = STATUS_PROGRESSION[idx + 1] ?? 'READY';
          return { ...f, status: next };
        });
        if (!hasInProgress(updated)) clearInterval(tick);
        return updated;
      });
    }, PREVIEW_TICK_MS);

    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode, files.map(f => f.id).join(',')]);

  useEffect(() => {
    if (!isPreviewMode && hasInProgress(files)) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode, files.map(f => `${f.id}:${f.status}`).join(',')]);

  const handleUploaded = (newFile: OrgAdminFile) => {
    setFiles(prev => [newFile, ...prev]);
  };

  const handleDeleted = (deletedFileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== deletedFileId));
    setFileToDelete(null);
  };

  return (
    <>
      <InfoCard
        title={t('orgAdmin.filesPanel.title')}
        action={
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={() => setUploadOpen(true)}
          >
            {t('actions.uploadArtifact')}
          </Button>
        }
      >
        {loadError && (
          <Box mb={1}>
            <Typography variant="body2" color="error">
              {loadError}
            </Typography>
          </Box>
        )}
        {files.length === 0 ? (
          <Typography variant="body2" className={classes.subtle}>
            {t('orgAdmin.filesPanel.empty')}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('table.name')}</TableCell>
                <TableCell>{t('table.typeTag')}</TableCell>
                <TableCell>{t('table.status')}</TableCell>
                <TableCell>{t('table.version')}</TableCell>
                <TableCell>{t('table.uploadedBy')}</TableCell>
                <TableCell>
                  <Tooltip
                    title={t('orgAdmin.filesPanel.isDefaultTooltip') as string}
                  >
                    <span>{t('orgAdmin.filesPanel.isDefaultHeader')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map(file => (
                <TableRow
                  key={file.id}
                  className={
                    file.status === 'FAILED' ? classes.failedRow : undefined
                  }
                >
                  <TableCell>{file.name}</TableCell>
                  <TableCell>{file.fileTypeTag}</TableCell>
                  <TableCell>
                    <ContextFileStatusChip
                      status={file.status}
                      errorReport={file.errorReport}
                    />
                  </TableCell>
                  <TableCell>{file.version}</TableCell>
                  <TableCell>{file.uploader?.displayName ?? '—'}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={file.isDefault}
                      disabled
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('contextFiles.deleteFile') as string}>
                      <IconButton
                        size="small"
                        aria-label={t('contextFiles.deleteFileNamed', {
                          name: file.name,
                        })}
                        onClick={() => setFileToDelete(file)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </InfoCard>

      <OrgAdminUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        orgId={orgId}
        apiOptions={apiOptions}
        isPreviewMode={isPreviewMode}
        onUploaded={handleUploaded}
      />

      <DeleteOrgFileDialog
        file={fileToDelete}
        orgId={orgId}
        apiOptions={apiOptions}
        isPreviewMode={isPreviewMode}
        onClose={() => setFileToDelete(null)}
        onDeleted={handleDeleted}
      />

      <Snackbar
        open={!!scanFailedMsg}
        autoHideDuration={6000}
        onClose={() => setScanFailedMsg(undefined)}
        ContentProps={{
          className: classes.snackbarError,
          'aria-live': 'assertive',
        }}
        message={<span data-testid="snackbar-message">{scanFailedMsg}</span>}
        action={
          <IconButton
            size="small"
            color="inherit"
            aria-label="close"
            onClick={() => setScanFailedMsg(undefined)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
}
